/**
 * openHtmlInNewTab — safely open an HTML response in a new browser tab.
 *
 * The naive pattern across the codebase looked like:
 *
 *   onClick={async () => {
 *     const r = await api.get('/some/path', { responseType: 'text' });
 *     const w = window.open('', '_blank');
 *     w.document.write(r.data);
 *     w.document.close();
 *   }}
 *
 * Three problems with that:
 *   1. The `await` between the click handler and `window.open()` causes
 *      modern browsers (Chrome, Safari, Brave, mobile) to lose the
 *      trusted-user-gesture context. The popup blocker silently returns
 *      `null`, and `null.document.write(...)` throws.
 *   2. Errors get swallowed by the surrounding try/catch with no useful
 *      feedback to the user.
 *   3. Network latency on production CDNs makes this race worse — users
 *      with a slower link see the bug more often.
 *
 * This helper:
 *   • Pre-opens a blank tab SYNCHRONOUSLY (before any await) so the
 *     gesture context is still trusted.
 *   • Writes a friendly "Preparing…" placeholder into the tab.
 *   • Runs the caller-supplied async fetcher.
 *   • Writes the fetched HTML into the pre-opened tab.
 *   • If the popup was blocked anyway, shows a clear toast and never
 *     hijacks the current tab.
 *
 * Usage:
 *   onClick={() => openHtmlInNewTab(
 *     () => api.get(`/fees/${id}/view`, { responseType: 'text' }),
 *     { label: 'Fee Receipt' },
 *   )}
 *
 *   // or with literal HTML:
 *   onClick={() => openHtmlInNewTab(buildReceiptHtml(...), { label: 'Receipt' })}
 */
import { toast } from 'sonner';

function _openPlaceholderTab(label) {
  let win = null;
  try {
    // Phase 264 — DO NOT pass `noopener,noreferrer` here. When `noopener` is
    // set, browsers intentionally return `null` from `window.open()` and the
    // pre-opened tab is unrecoverable — it sits stuck on about:blank forever
    // because we can't write to its document. We strip `window.opener` AFTER
    // we successfully write content (below) to preserve the security intent
    // without losing the window handle. See MDN: window.open#noopener.
    win = window.open('about:blank', '_blank');
    if (win && win.document) {
      win.document.write(
        `<!doctype html><html><head><title>Loading ${label}…</title>` +
        `<meta charset="utf-8" /></head>` +
        `<body style="font-family:-apple-system,Segoe UI,sans-serif;padding:48px;` +
        `color:#475569;background:#f8fafc">` +
        `<h2 style="color:#0f172a;margin:0 0 8px 0">Preparing your ${(label || 'document').toLowerCase()}…</h2>` +
        `<p style="margin:0">This tab will refresh once it's ready.</p>` +
        `</body></html>`,
      );
    }
  } catch (e) {
    win = null;
  }
  return win;
}

/**
 * @param {Function|string|Promise} fetcherOrHtml
 *   • Either a callable that returns a Promise resolving to either:
 *     - an axios-style response with `.data` containing HTML, or
 *     - a plain HTML string
 *   • Or literal HTML string for synchronous content
 *   • Or a Promise resolving to the above
 * @param {object} [opts]
 * @param {string} [opts.label]   Friendly document label for placeholder + toast
 * @param {string} [opts.errorMessage]  Custom toast on failure
 */
export async function openHtmlInNewTab(fetcherOrHtml, opts = {}) {
  const label = opts.label || 'Document';
  const win = _openPlaceholderTab(label);
  const _closeWin = () => {
    if (win && !win.closed) {
      try { win.close(); } catch (e) { /* noop */ }
    }
  };

  let html;
  try {
    if (typeof fetcherOrHtml === 'function') {
      const resp = await fetcherOrHtml();
      html = resp?.data ?? resp;
    } else if (fetcherOrHtml && typeof fetcherOrHtml.then === 'function') {
      const resp = await fetcherOrHtml;
      html = resp?.data ?? resp;
    } else {
      html = fetcherOrHtml;
    }
    if (typeof html !== 'string') {
      throw new Error(`openHtmlInNewTab expected a string but got ${typeof html}`);
    }
  } catch (e) {
    _closeWin();
    try {
      toast.error(opts.errorMessage || `Failed to load ${label.toLowerCase()}`);
    } catch (te) { /* sonner unavailable */ }
    return { ok: false, reason: e?.message || 'fetch_failed' };
  }

  if (win && !win.closed) {
    try {
      win.document.open();
      win.document.write(html);
      win.document.close();
      // Phase 264 — Strip opener AFTER we've finished writing content,
      // preserving the security intent of `noopener` without breaking the
      // synchronous handle we needed to pre-open the tab.
      try { win.opener = null; } catch (_e) { /* noop */ }
      return { ok: true, mode: 'tab' };
    } catch (e) {
      _closeWin();
      try { toast.error(`Failed to render ${label.toLowerCase()}`); } catch (te) { /* noop */ }
      return { ok: false, reason: 'write_failed' };
    }
  }

  // Popup blocked — surface a clean toast and DON'T hijack the current tab
  try {
    toast.error(`Popup blocked`, {
      description: `Allow popups for this site to open ${label.toLowerCase()}s in a new tab.`,
    });
  } catch (te) { /* noop */ }
  return { ok: false, reason: 'popup_blocked' };
}

export default openHtmlInNewTab;
